import { useEffect, useState } from 'react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import Tooltip from 'components/elements/Tooltip';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { SidebarFilterToggle } from 'components/layout/SidebarLayout';
import PolygonFilter from 'components/widgets/PolygonFilter';
import DateCreatedOverlayFilter from 'components/widgets/ProjectInsights/OverlayMenu/DateCreatedOverlayFilter';
import DateUpdatedOverlayFilter from 'components/widgets/ProjectInsights/OverlayMenu/DateUpdatedOverlayFilter';
import StatusOverlayFilter from 'components/widgets/ProjectInsights/OverlayMenu/StatusOverlayFilter';
import ProjectSearchbar from 'components/widgets/ProjectInsights/ProjectSearchbar';
import { isChatCreatedSavedSearch } from 'components/widgets/ProjectInsights/Toolbar/getSavedSearchEditorType';
import { useSelectedGroupConfig } from 'components/widgets/ProjectInsights/Toolbar/useSelectedGroupConfig';
import {
  useHasPlacedFirstPolygonDrawingPoint,
  useIsPolygonDrawingEnabled,
  useSelectedPolygonBoundsOnMap,
} from 'state/map';

const searchInputContainer = {
  position: 'absolute',
  top: 0,
  left: 0,
  padding: 'padding_24',
  width: '100%',
  justifyContent: 'space-between',
  gap: 'padding_24',
  alignItems: 'flex-start',
};

const drawingOverlayStyle = {
  ...searchInputContainer,
  alignItems: 'center',

  ':before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#000',
    opacity: '0.7',
  },
};

const menuTooltipStyle = {
  color: Colors.White,
  zIndex: 1,
};

const menuButtonsStyle = {
  gap: 'padding_16',
  alignItems: 'flex-end',
};

const leftColumnElements = {
  gap: 'padding_16',
  flex: 1,
  maxWidth: '600px',
};

const addressSearchRowStyle = {
  gap: 'padding_8',
  alignItems: 'stretch',
  width: '100%',
};

const addressSearchStyle = {
  flex: 1,
};

const menuStyle = {
  alignItems: 'center',
  gap: 'padding_8',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(0, 188px))',
  justifyContent: 'flex-start',
};

const OverlayMenu = () => {
  const [isPolygonDrawingEnabled, setIsPolygonDrawingEnabled] =
    useIsPolygonDrawingEnabled();
  const [selectedPolygonBounds, setSelectedPolygonBounds] =
    useSelectedPolygonBoundsOnMap();
  const [hasPlacedFirstPolygonDrawingPoint] = useHasPlacedFirstPolygonDrawingPoint();
  const [backupSelectedPolygonBounds, setBackupSelectedPolygonBounds] = useState('');
  // AI saved searches are defined entirely by the chat's raw queries and can't
  // be narrowed with a hand-drawn area, so drawing is disabled for them.
  const selectedGroupConfig = useSelectedGroupConfig();
  const isAiSearch = isChatCreatedSavedSearch(selectedGroupConfig);

  // If the user starts a draw session and then switches to an AI saved search,
  // force-cancel drawing so an active session can't be completed against an
  // AI search. Deliberately do NOT restore backupSelectedPolygonBounds here —
  // restoring could re-apply a polygon to an AI search.
  useEffect(() => {
    if (isAiSearch && isPolygonDrawingEnabled) {
      setIsPolygonDrawingEnabled(false);
    }
  }, [isAiSearch, isPolygonDrawingEnabled, setIsPolygonDrawingEnabled]);

  const handleDrawAreaClick = () => {
    setIsPolygonDrawingEnabled(!isPolygonDrawingEnabled);
    setBackupSelectedPolygonBounds(selectedPolygonBounds);
    setSelectedPolygonBounds('');
  };

  const handleCancelDrawing = () => {
    setIsPolygonDrawingEnabled(false);
    if (backupSelectedPolygonBounds) {
      setSelectedPolygonBounds(backupSelectedPolygonBounds);
    }
  };

  const tooltipLabel = !hasPlacedFirstPolygonDrawingPoint
    ? 'Draw a shape by clicking points on the map'
    : 'Click to start drawing an area';

  const onClearSelectedAreaClick = () => {
    setSelectedPolygonBounds('');
  };

  if (isPolygonDrawingEnabled) {
    return (
      <FlexRow sx={drawingOverlayStyle}>
        <Text.Large sx={menuTooltipStyle}>{tooltipLabel}</Text.Large>
        <Button variant="primary" onClick={handleCancelDrawing}>
          Cancel
        </Button>
      </FlexRow>
    );
  }

  return (
    <FlexRow sx={searchInputContainer}>
      <FlexColumn sx={leftColumnElements}>
        <FlexRow sx={addressSearchRowStyle}>
          <SidebarFilterToggle />
          <ProjectSearchbar sx={addressSearchStyle} />
        </FlexRow>
        <FlexRow sx={menuStyle}>
          <DateCreatedOverlayFilter />
          <DateUpdatedOverlayFilter />
          <StatusOverlayFilter />
        </FlexRow>
      </FlexColumn>
      <FlexColumn sx={menuButtonsStyle}>
        <Tooltip
          tooltipText="Drawing an area isn't available for AI saved searches"
          shouldRenderTooltip={isAiSearch}
          placement="bottom"
        >
          <Button
            variant="secondary"
            onClick={handleDrawAreaClick}
            startAdornment={<Icon name={Icon.Name.PolygonArea} />}
            disabled={isAiSearch}
          >
            Draw area
          </Button>
        </Tooltip>
        {selectedPolygonBounds && (
          <Button
            variant="alert"
            onClick={onClearSelectedAreaClick}
            startAdornment={<Icon name={Icon.Name.CloseX} />}
          >
            Clear area
          </Button>
        )}
        <PolygonFilter />
      </FlexColumn>
    </FlexRow>
  );
};

export default OverlayMenu;
